import type { InfiniteData } from '@tanstack/react-query';
import type { ChatMessageDto, ChatPageDto, DecisionDto, PostDto, ReactionDto, TalkLevel, TaskDto, TopicDetailDto, TopicDto } from '@werft/contracts';
import type { OutboxEntry } from '../../platform/outbox.js';
import { t } from '../../i18n/index.js';
import { api, ApiError, newOpId, newUuid } from '../../platform/api.js';
import { keys, queryClient } from '../../platform/queries.js';
import { upsertMessage, upsertPost, upsertTask, upsertTopic } from '../../platform/realtime.js';
import { resendWithSafeList, safeListHandled } from '../../platform/derive.js';
import { outbox } from '../../platform/sync.js';
import { toast } from '../../platform/toast.js';

/**
 * Schreibwege der Gespräche (Paket P13). Themen, Antworten und Chat-Nachrichten bekommen ihre ID im Browser und
 * erscheinen sofort (Overlay), auch offline; der Ausgangskorb sendet sie mit derselben opId. Lesemarken gehen direkt
 * (verlorene Lesemarken schaden nicht).
 */

const failed = (err: unknown) => {
  if (err instanceof ApiError && err.status === 404) return;
  toast(t('sync.failed', { message: err instanceof Error ? err.message : t('task.failed') }), { kind: 'error' });
};
const reload = (pid: string) => {
  void queryClient.invalidateQueries({ queryKey: ['p', pid, 'topics'] });
  void queryClient.invalidateQueries({ queryKey: ['p', pid, 'topic'] });
  void queryClient.invalidateQueries({ queryKey: keys.chat(pid) });
};

outbox.register('talk.topic', {
  done: (e, r) => {
    upsertTopic(e.projectId, r as TopicDto);
    // Ein eben angelegtes Thema war offen, bevor der Server es kannte
    void queryClient.invalidateQueries({ queryKey: keys.topic(e.projectId, (r as TopicDto).id), refetchType: 'active' });
  }, fail: (e, err) => (reload(e.projectId), failed(err)) });
outbox.register('talk.post', { done: (e, r) => upsertPost(e.projectId, r as PostDto), fail: (e, err) => (reload(e.projectId), failed(err)) });
outbox.register('talk.message', { done: (e, r) => upsertMessage(e.projectId, r as ChatMessageDto), fail: (e, err) => (reload(e.projectId), failed(err)) });
outbox.register('talk.remove', { done: (e) => reload(e.projectId), fail: (e, err) => (reload(e.projectId), failed(err)) });
outbox.register('talk.react', { done: (e, r) => applyReactions(e.projectId, e.overlay, (r as { reactions: ReactionDto[] }).reactions), fail: (e, err) => (reload(e.projectId), failed(err)) });
outbox.register('talk.level', {
  done: (e) => void queryClient.invalidateQueries({ queryKey: keys.talk(e.projectId) }),
  fail: (e, err) => (void queryClient.invalidateQueries({ queryKey: keys.talk(e.projectId) }), failed(err)),
});
outbox.register('talk.task', {
  done: (e, r) => {
    const task = (r as { task: TaskDto }).task;
    upsertTask(e.projectId, task);
    reload(e.projectId);
    toast(t('talk.taskCreated', { title: task.title }));
  },
  fail: (e, err) => (reload(e.projectId), void (safeListHandled(err, () => resendWithSafeList(e)) || failed(err))),
});

/** Briefing AF24: Ergebnis festhalten/zurücknehmen – Antwort ist das Thema; der Verlauf kommt mit dem Thema neu */
outbox.register('talk.result', {
  done: (e, r) => {
    upsertTopic(e.projectId, r as TopicDto);
    void queryClient.invalidateQueries({ queryKey: keys.topic(e.projectId, (r as TopicDto).id) });
  },
  fail: (e, err) => (reload(e.projectId), failed(err)),
});

/** Ergebnis festhalten: eigene Zusammenfassung oder eine Antwort (deren Text gilt als Stand) */
export function setTopicResult(tp: TopicDto, input: { text?: string; postId?: string | null }) {
  const opId = newOpId();
  void outbox.enqueue({ opId, kind: 'talk.result', projectId: tp.projectId, method: 'PUT', url: `/api/topics/${tp.id}/result`, body: { opId, ...input } }).catch(() => {});
  toast(t('result.saved', { title: tp.title }));
}

export function clearTopicResult(tp: TopicDto) {
  const opId = newOpId();
  void outbox.enqueue({ opId, kind: 'talk.result', projectId: tp.projectId, method: 'DELETE', url: `/api/topics/${tp.id}/result`, body: { opId } }).catch(() => {});
  toast(t('result.cleared', { title: tp.title }));
}

outbox.register('talk.decision', {
  done: (e, r) => {
    const d = r as DecisionDto;
    void queryClient.invalidateQueries({ queryKey: keys.topic(e.projectId, d.topicId) });
    void queryClient.invalidateQueries({ queryKey: ['p', e.projectId, 'topics'] });
    void queryClient.invalidateQueries({ queryKey: keys.overview(e.projectId) });
    if (d.taskId) void queryClient.invalidateQueries({ queryKey: keys.project(e.projectId) });
  },
  fail: (e, err) => (reload(e.projectId), void (safeListHandled(err, () => resendWithSafeList(e, 'followUp')) || failed(err))),
});

/** Antwort einer Reaktion übernehmen, bevor das Overlay wegfällt (kein Flackern, falls das Ereignis später kommt) */
function applyReactions(pid: string, o: OutboxEntry['overlay'], reactions: ReactionDto[]) {
  if (o?.type === 'talkTopicPatch') {
    const cur = queryClient.getQueryData<TopicDetailDto>(keys.topic(pid, o.topicId))?.topic ?? queryClient.getQueryData<{ items: TopicDto[] }>(keys.topics(pid, 'open'))?.items.find((x) => x.id === o.topicId);
    if (cur) upsertTopic(pid, { ...cur, reactions });
  } else if (o?.type === 'talkPostPatch') {
    const cur = queryClient.getQueryData<TopicDetailDto>(keys.topic(pid, o.topicId))?.posts.find((x) => x.id === o.postId);
    if (cur) upsertPost(pid, { ...cur, reactions });
  } else if (o?.type === 'talkMessagePatch') {
    const cur = queryClient.getQueryData<InfiniteData<ChatPageDto>>(keys.chat(pid))?.pages.flatMap((pg) => pg.items).find((x) => x.id === o.messageId);
    if (cur) upsertMessage(pid, { ...cur, reactions });
  }
}

/* ---------- Themen ---------- */

export function createTopic(projectId: string, meId: string, title: string, body: string, areaId: string | null = null, decisionNeeded = false): string {
  const opId = newOpId();
  const id = newUuid();
  const now = new Date().toISOString();
  const topic: TopicDto = {
    id,
    projectId,
    title,
    body,
    authorId: meId,
    reactions: [],
    replyCount: 0,
    lastActivityAt: now,
    lastAuthorId: meId,
    resolvedAt: null,
    resolvedBy: null,
    taskId: null,
    areaId,
    decisionNeeded,
    decision: null,
    result: null,
    createdAt: now,
    editedAt: null,
    version: 0,
  };
  void outbox
    .enqueue({ opId, kind: 'talk.topic', projectId, method: 'POST', url: `/api/projects/${projectId}/topics`, body: { opId, id, title, body, ...(areaId ? { areaId } : {}), ...(decisionNeeded ? { decisionNeeded } : {}) }, overlay: { type: 'talkTopic', topic } })
    .catch(() => {});
  return id;
}

export function editTopic(tp: TopicDto, patch: { title?: string; body?: string }) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'talk.topic',
      projectId: tp.projectId,
      method: 'PATCH',
      url: `/api/topics/${tp.id}`,
      body: { opId, expectedVersion: tp.version, patch },
      overlay: { type: 'talkTopicPatch', topicId: tp.id, fields: { ...patch, editedAt: new Date().toISOString() } },
    })
    .catch(() => {});
}

export function resolveTopic(tp: TopicDto, meId: string, resolved: boolean) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'talk.topic',
      projectId: tp.projectId,
      method: 'PATCH',
      url: `/api/topics/${tp.id}`,
      body: { opId, expectedVersion: tp.version, patch: { resolved } },
      overlay: { type: 'talkTopicPatch', topicId: tp.id, fields: resolved ? { resolvedAt: new Date().toISOString(), resolvedBy: meId } : { resolvedAt: null, resolvedBy: null } },
    })
    .then((r) => {
      // AF09: geklärt → „Entsteht daraus eine Aufgabe?“ (nach Bestätigung, nur aus diesem Tab)
      if (r && resolved) void import('../hints/triggers.js').then((m) => m.afterResolve(tp.projectId, tp.id));
    })
    .catch(() => {});
  toast(resolved ? t('talk.resolved', { title: tp.title }) : t('talk.reopened', { title: tp.title }), resolved ? { action: { label: t('common.undo'), run: () => resolveTopic({ ...tp, version: tp.version + 1 }, meId, false) } } : undefined);
}

/** Audit 0.36 F09: „Entscheidung nötig“ an- oder abschalten */
export function setDecisionNeeded(tp: TopicDto, on: boolean) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'talk.topic',
      projectId: tp.projectId,
      method: 'PATCH',
      url: `/api/topics/${tp.id}`,
      body: { opId, expectedVersion: tp.version, patch: { decisionNeeded: on } },
      overlay: { type: 'talkTopicPatch', topicId: tp.id, fields: { decisionNeeded: on } },
    })
    .catch(() => {});
}

/** Audit 0.36 F09: Entscheidung festhalten (Bezug und Folgeaufgabe optional); Ergebnis kommt nach dem Speichern */
export function recordDecision(tp: TopicDto, input: { text: string; ref: { kind: 'page' | 'file'; id: string } | null; followUp?: { title: string; listId?: string } }) {
  const opId = newOpId();
  const id = newUuid();
  void outbox
    .enqueue({ opId, kind: 'talk.decision', projectId: tp.projectId, method: 'POST', url: `/api/topics/${tp.id}/decisions`, body: { opId, id, ...input }, overlay: { type: 'talkTopicPatch', topicId: tp.id, fields: { resolvedAt: tp.resolvedAt ?? new Date().toISOString() } } })
    .catch(() => {});
  toast(t('decision.saved', { title: tp.title }));
  return id;
}

export function deleteTopic(tp: TopicDto) {
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'talk.remove', projectId: tp.projectId, method: 'DELETE', url: `/api/topics/${tp.id}`, body: { opId }, overlay: { type: 'talkTopicRemove', topicId: tp.id } })
    .catch(() => {});
}

export function markTopicRead(projectId: string, topicId: string) {
  void api.post(`/api/topics/${topicId}/read`, { opId: newOpId() }).then(
    () => void queryClient.invalidateQueries({ queryKey: keys.talk(projectId) }),
    () => {},
  );
}

/* ---------- Antworten ---------- */

export function addPost(projectId: string, topicId: string, meId: string, body: string) {
  const opId = newOpId();
  const id = newUuid();
  const post: PostDto = { id, projectId, topicId, body, authorId: meId, reactions: [], taskId: null, createdAt: new Date().toISOString(), editedAt: null, version: 0 };
  void outbox
    .enqueue({ opId, kind: 'talk.post', projectId, method: 'POST', url: `/api/topics/${topicId}/posts`, body: { opId, id, body }, overlay: { type: 'talkPost', post } })
    .catch(() => {});
}

export function editPost(p: PostDto, body: string) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'talk.post',
      projectId: p.projectId,
      method: 'PATCH',
      url: `/api/talk-posts/${p.id}`,
      body: { opId, expectedVersion: p.version, body },
      overlay: { type: 'talkPostPatch', topicId: p.topicId, postId: p.id, fields: { body, editedAt: new Date().toISOString() } },
    })
    .catch(() => {});
}

export function deletePost(p: PostDto) {
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'talk.remove', projectId: p.projectId, method: 'DELETE', url: `/api/talk-posts/${p.id}`, body: { opId }, overlay: { type: 'talkPostRemove', topicId: p.topicId, postId: p.id } })
    .catch(() => {});
}

/* ---------- Chat ---------- */

export function sendMessage(projectId: string, meId: string, body: string) {
  const opId = newOpId();
  const id = newUuid();
  const message: ChatMessageDto = { id, projectId, body, authorId: meId, reactions: [], taskId: null, createdAt: new Date().toISOString(), editedAt: null, version: 0 };
  void outbox
    .enqueue({ opId, kind: 'talk.message', projectId, method: 'POST', url: `/api/projects/${projectId}/chat`, body: { opId, id, body }, overlay: { type: 'talkMessage', message } })
    .catch(() => {});
}

export function editMessage(m: ChatMessageDto, body: string) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'talk.message',
      projectId: m.projectId,
      method: 'PATCH',
      url: `/api/talk-messages/${m.id}`,
      body: { opId, expectedVersion: m.version, body },
      overlay: { type: 'talkMessagePatch', messageId: m.id, fields: { body, editedAt: new Date().toISOString() } },
    })
    .catch(() => {});
}

export function deleteMessage(m: ChatMessageDto) {
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'talk.remove', projectId: m.projectId, method: 'DELETE', url: `/api/talk-messages/${m.id}`, body: { opId }, overlay: { type: 'talkMessageRemove', messageId: m.id } })
    .catch(() => {});
}

let readTimer: ReturnType<typeof setTimeout> | undefined;
/** Chat bis hierhin gelesen (gebündelt) */
export function markChatRead(projectId: string, at: string) {
  queryClient.setQueryData(keys.talk(projectId), (old: { chatReadAt: string | null; chatUnread: number } | undefined) =>
    old ? { ...old, chatUnread: 0, chatReadAt: !old.chatReadAt || old.chatReadAt < at ? at : old.chatReadAt } : old,
  );
  clearTimeout(readTimer);
  readTimer = setTimeout(() => void api.post(`/api/projects/${projectId}/talk/read`, { opId: newOpId(), at }).catch(() => {}), 800);
}

/* ---------- Gemeinsam ---------- */

export type TalkKind = 'topic' | 'post' | 'message';
const base = { topic: '/api/topics', post: '/api/talk-posts', message: '/api/talk-messages' } as const;

/** Reaktion umschalten; sichtbar sofort über das passende Overlay */
export function react(kind: TalkKind, x: { id: string; projectId: string; reactions: ReactionDto[]; topicId?: string }, emoji: string, meId: string) {
  const mine = x.reactions.find((r) => r.emoji === emoji)?.userIds.includes(meId) ?? false;
  const reactions = toggled(x.reactions, emoji, meId, !mine);
  const opId = newOpId();
  const overlay =
    kind === 'topic'
      ? ({ type: 'talkTopicPatch', topicId: x.id, fields: { reactions } } as const)
      : kind === 'post'
        ? ({ type: 'talkPostPatch', topicId: x.topicId!, postId: x.id, fields: { reactions } } as const)
        : ({ type: 'talkMessagePatch', messageId: x.id, fields: { reactions } } as const);
  void outbox
    .enqueue({ opId, kind: 'talk.react', projectId: x.projectId, method: 'PUT', url: `${base[kind]}/${x.id}/reactions`, body: { opId, emoji, on: !mine }, overlay })
    .catch(() => {});
}

function toggled(list: ReactionDto[], emoji: string, me: string, on: boolean): ReactionDto[] {
  const has = list.some((r) => r.emoji === emoji);
  const next = has
    ? list.map((r) => (r.emoji === emoji ? { ...r, userIds: on ? [...new Set([...r.userIds, me])] : r.userIds.filter((u) => u !== me) } : r))
    : on
      ? [...list, { emoji, userIds: [me] }]
      : list;
  return next.filter((r) => r.userIds.length);
}

/** Aus einer Nachricht eine Aufgabe machen (Titel frei, Liste wählbar) */
export function toTask(kind: TalkKind, projectId: string, id: string, title: string, listId?: string) {
  const opId = newOpId();
  void outbox.enqueue({ opId, kind: 'talk.task', projectId, method: 'POST', url: `${base[kind]}/${id}/task`, body: { opId, title, ...(listId ? { listId } : {}) } }).catch(() => {});
}

export function setLevel(projectId: string, level: TalkLevel) {
  const opId = newOpId();
  queryClient.setQueryData(keys.talk(projectId), (old: { level: TalkLevel } | undefined) => (old ? { ...old, level } : old));
  void outbox.enqueue({ opId, kind: 'talk.level', projectId, method: 'PUT', url: `/api/projects/${projectId}/talk/settings`, body: { opId, level } }).catch(() => {});
}

/** Erste Zeile als Vorschlag für den Aufgabentitel (Erwähnungen als Namen) */
export function suggestTitle(text: string): string {
  const line = text.split('\n').find((l) => l.trim()) ?? '';
  const s = line.trim();
  return s.length > 120 ? `${s.slice(0, 119)}…` : s;
}
